// ============================================================
// LiveKitSetupCard — reusable card embedded on every creator
// dashboard (Studio, CreatorHQ, StudioTools, Settings,
// OwnerMobileGoLive). Handles the full multi-project lifecycle:
// list, add (with tutorial), activate, test, remove.
// Collapsible by default so it doesn't dominate the page.
// ============================================================
import { useEffect, useState, forwardRef, useImperativeHandle } from "react";
import { toast } from "sonner";
import {
  Radio, ExternalLink, CheckCircle2, AlertCircle, Plug, Loader2,
  ChevronDown, ChevronUp, Plus, Trash2, Zap, Activity, RefreshCcw,
} from "lucide-react";
import api from "../lib/api";

const LiveKitSetupCard = forwardRef(function LiveKitSetupCard(
  { defaultOpen = false, compact = false, title = "LiveKit Cloud", subtitle = null },
  ref,
) {
  const [projects, setProjects] = useState(null); // null = loading, [] = none
  const [busy, setBusy] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [open, setOpen] = useState(defaultOpen);
  const [form, setForm] = useState({ label: "", url: "", api_key: "", api_secret: "" });
  const [testingId, setTestingId] = useState(null);

  const load = async () => {
    try { const { data } = await api.get("/me/livekit/projects"); setProjects(data.projects || []); }
    catch { setProjects([]); }
  };
  useEffect(() => { load(); }, []);

  // Expose an imperative "open + scroll into view" method so parent pages can
  // pop the card in-place when the user hits the "connect first" gate.
  useImperativeHandle(ref, () => ({
    openAndScroll: () => {
      setOpen(true);
      setShowAddForm(true);
      setTimeout(() => {
        document.querySelector('[data-livekit-setup-anchor]')?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 60);
    },
  }));

  const submit = async () => {
    if (busy) return;
    const { label, url, api_key, api_secret } = form;
    if (!label.trim() || !url.trim() || !api_key.trim() || !api_secret.trim()) {
      toast.error("All four fields are required");
      return;
    }
    setBusy(true);
    try {
      await api.post("/me/livekit/projects", { label: label.trim(), url: url.trim(), api_key: api_key.trim(), api_secret: api_secret.trim() });
      toast.success("LiveKit project connected");
      setForm({ label: "", url: "", api_key: "", api_secret: "" });
      setShowAddForm(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add project");
    } finally { setBusy(false); }
  };

  const activate = async (p) => {
    setBusy(true);
    try { await api.post(`/me/livekit/projects/${p.id}/activate`); toast.success(`${p.label} is now active`); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Activate failed"); }
    finally { setBusy(false); }
  };

  const testOne = async (p) => {
    setTestingId(p.id);
    try {
      const { data } = await api.post(`/me/livekit/projects/${p.id}/test`);
      if (data.ok) toast.success(`${p.label}: healthy`);
      else toast.error(`${p.label}: ${data.error}`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Test failed"); }
    finally { setTestingId(null); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Remove "${p.label}"? This cannot be undone.`)) return;
    setBusy(true);
    try { await api.delete(`/me/livekit/projects/${p.id}`); toast.success("Removed"); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Remove failed"); }
    finally { setBusy(false); }
  };

  const connected = Array.isArray(projects) && projects.length > 0;
  const statusColor = connected ? "#00FF66" : "#FFC107";

  return (
    <section
      data-livekit-setup-anchor
      data-testid="livekit-setup-card"
      className={`glass ${compact ? "p-4" : "p-5"} space-y-3`}
      style={{ borderLeft: `2px solid ${statusColor}` }}
    >
      {/* HEADER — always visible, collapsible */}
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center justify-between w-full text-left group"
        data-testid="livekit-card-toggle"
      >
        <div className="flex items-center gap-3">
          <Radio className="w-5 h-5" style={{ color: statusColor }} />
          <div>
            <div className="label-eyebrow" style={{ color: statusColor }}>// live streaming setup</div>
            <div className="font-display font-black text-lg mt-0.5">{title}</div>
            {subtitle && <div className="text-[11px] text-white/50 mt-0.5">{subtitle}</div>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {projects !== null && (
            <span
              className={`px-2.5 py-1 rounded-full text-[10px] font-display tracking-widest uppercase inline-flex items-center gap-1 ${connected ? "bg-nexus-mint/15 text-nexus-mint" : "bg-nexus-amber/15 text-nexus-amber"}`}
              data-testid="livekit-status-badge"
            >
              {connected
                ? <><CheckCircle2 className="w-3 h-3" /> {projects.length} connected</>
                : <><AlertCircle className="w-3 h-3" /> Not Connected</>}
            </span>
          )}
          {open ? <ChevronUp className="w-4 h-4 text-white/40" /> : <ChevronDown className="w-4 h-4 text-white/40" />}
        </div>
      </button>

      {!open && !connected && (
        <p className="text-[11px] text-white/50 pt-1">Connect your free LiveKit Cloud project to start going live. Click to expand.</p>
      )}

      {open && projects === null && (
        <div className="text-xs text-white/50 flex items-center gap-2 pt-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…</div>
      )}

      {open && projects !== null && (
        <>
          {/* ===== EMPTY STATE ===== */}
          {!connected && (
            <div className="space-y-3 pt-2">
              <p className="text-sm text-white/70">
                Each creator uses their own free-tier LiveKit Cloud project so ingress quota is never shared — you can always go live even when other creators are streaming. Add up to 10 projects and the platform will auto-rotate to a healthy one if one hits its cap.
              </p>
              <button
                onClick={() => setShowTutorial((s) => !s)}
                className="text-[11px] text-nexus-cyan tracking-widest uppercase font-display hover:underline"
                data-testid="livekit-tutorial-toggle"
              >
                {showTutorial ? "hide" : "show"} step-by-step tutorial ↓
              </button>
              {showTutorial && <Tutorial />}
              <AddForm form={form} setForm={setForm} onSubmit={submit} busy={busy} />
            </div>
          )}

          {/* ===== POPULATED STATE ===== */}
          {connected && (
            <div className="space-y-2 pt-1">
              <div className="text-[10px] text-white/40 tracking-widest uppercase">// projects ({projects.length}/10)</div>
              {projects.map((p) => (
                <div key={p.id} className={`border rounded-lg p-3 flex flex-wrap items-center gap-2 ${p.is_active ? "border-nexus-mint/40 bg-nexus-mint/5" : p.last_error ? "border-nexus-crimson/30 bg-nexus-crimson/5" : "border-white/10"}`} data-testid={`livekit-project-${p.id}`}>
                  <div className="flex-1 min-w-[220px]">
                    <div className="flex items-center gap-2">
                      <div className="text-sm font-medium">{p.label}</div>
                      {p.is_active && (
                        <span className="text-[9px] tracking-widest uppercase text-nexus-mint bg-nexus-mint/15 px-1.5 py-0.5 rounded font-display inline-flex items-center gap-1">
                          <Zap className="w-2.5 h-2.5" /> active
                        </span>
                      )}
                      {!p.is_active && !p.last_error && (
                        <span className="text-[9px] tracking-widest uppercase text-white/50 bg-white/5 px-1.5 py-0.5 rounded font-display">standby</span>
                      )}
                      {p.last_error && (
                        <span className="text-[9px] tracking-widest uppercase text-nexus-crimson bg-nexus-crimson/15 px-1.5 py-0.5 rounded font-display inline-flex items-center gap-1">
                          <AlertCircle className="w-2.5 h-2.5" /> error
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-white/50 font-mono break-all mt-0.5">{p.url}</div>
                    <div className="text-[10px] text-white/30 font-mono mt-0.5">
                      key: {p.api_key_masked} · secret: {p.api_secret_masked}
                    </div>
                    {p.last_error && (
                      <div className="text-[11px] text-nexus-crimson mt-1 break-words" data-testid={`livekit-project-error-${p.id}`}>
                        {p.last_error}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1 flex-wrap">
                    {!p.is_active && (
                      <button onClick={() => activate(p)} disabled={busy} className="btn-ghost !py-1 !px-2 text-[10px]" data-testid={`activate-${p.id}`}>
                        <Zap className="w-3 h-3" /> Activate
                      </button>
                    )}
                    <button onClick={() => testOne(p)} disabled={testingId === p.id} className="btn-ghost !py-1 !px-2 text-[10px]" data-testid={`test-${p.id}`}>
                      {testingId === p.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Activity className="w-3 h-3" />} Test
                    </button>
                    <button onClick={() => remove(p)} disabled={busy} className="btn-ghost !py-1 !px-2 text-[10px] !border-nexus-crimson !text-nexus-crimson" data-testid={`remove-${p.id}`}>
                      <Trash2 className="w-3 h-3" /> Remove
                    </button>
                  </div>
                </div>
              ))}
              {!showAddForm && projects.length < 10 && (
                <button
                  onClick={() => { setShowAddForm(true); setShowTutorial(false); }}
                  className="btn-ghost mt-2"
                  data-testid="livekit-add-another"
                >
                  <Plus className="w-3.5 h-3.5" /> Add another project
                </button>
              )}
              {showAddForm && (
                <div className="pt-3 border-t border-white/10 mt-2">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-[10px] tracking-widest uppercase text-white/40 font-display">// add another project</div>
                    <button onClick={() => setShowTutorial((s) => !s)} className="text-[10px] text-nexus-cyan hover:underline">
                      {showTutorial ? "hide" : "show"} tutorial
                    </button>
                  </div>
                  {showTutorial && <Tutorial />}
                  <AddForm form={form} setForm={setForm} onSubmit={submit} busy={busy} />
                </div>
              )}
              <button
                onClick={load}
                className="text-[10px] text-white/40 tracking-widest uppercase hover:text-white/70 inline-flex items-center gap-1 mt-2"
                data-testid="livekit-refresh"
              >
                <RefreshCcw className="w-3 h-3" /> Refresh
              </button>
            </div>
          )}
        </>
      )}
    </section>
  );
});

function Tutorial() {
  return (
    <div className="border border-nexus-cyan/30 bg-nexus-cyan/5 p-4 rounded-lg space-y-2 text-xs text-white/80" data-testid="livekit-tutorial">
      <div className="font-display font-bold text-nexus-cyan">// how to get your keys (60 seconds)</div>
      <ol className="list-decimal list-outside pl-5 space-y-1.5">
        <li>
          Go to{" "}
          <a href="https://cloud.livekit.io" target="_blank" rel="noopener noreferrer" className="text-nexus-cyan underline inline-flex items-center gap-1">
            cloud.livekit.io <ExternalLink className="w-3 h-3" />
          </a>
        </li>
        <li>Sign up free with Google or GitHub</li>
        <li>Create a project (any name)</li>
        <li>Open <b>Settings → Keys</b>, click <b>Add key</b>, copy the <b>API Key</b> and <b>API Secret</b> (secret shows once)</li>
        <li>Copy the <b>WebSocket URL</b> from the project dashboard — looks like <span className="font-mono">wss://your-project.livekit.cloud</span></li>
        <li>Paste all three into the form below and click <b>Add project</b></li>
      </ol>
    </div>
  );
}

function AddForm({ form, setForm, onSubmit, busy }) {
  return (
    <div className="space-y-2">
      <input
        value={form.label}
        onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
        placeholder="Label (e.g. My Main Account)"
        className="w-full bg-transparent border border-white/15 px-3 py-2 text-sm"
        data-testid="livekit-label-input"
      />
      <input
        value={form.url}
        onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))}
        placeholder="wss://your-project.livekit.cloud"
        className="w-full bg-transparent border border-white/15 px-3 py-2 text-sm font-mono"
        data-testid="livekit-url-input"
      />
      <input
        value={form.api_key}
        onChange={(e) => setForm((f) => ({ ...f, api_key: e.target.value }))}
        placeholder="API Key (APIxxxxxxxxxxx)"
        className="w-full bg-transparent border border-white/15 px-3 py-2 text-sm font-mono"
        data-testid="livekit-key-input"
      />
      <input
        type="password"
        value={form.api_secret}
        onChange={(e) => setForm((f) => ({ ...f, api_secret: e.target.value }))}
        placeholder="API Secret (shown once at key creation)"
        className="w-full bg-transparent border border-white/15 px-3 py-2 text-sm font-mono"
        data-testid="livekit-secret-input"
      />
      <button
        onClick={onSubmit}
        disabled={busy}
        className="btn-nexus disabled:opacity-40"
        data-testid="livekit-add-btn"
      >
        {busy ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Verifying…</> : <><Plug className="w-3.5 h-3.5" /> Add project</>}
      </button>
    </div>
  );
}

// Small reusable modal that gates go-live actions. It intentionally does NOT
// navigate away — instead it lets the caller open the embedded card in-place.
export function LiveKitConnectModal({ open, onClose, onSetupClick }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/80 p-4" onClick={onClose} data-testid="livekit-connect-modal">
      <div className="glass-strong max-w-md w-full p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-nexus-amber/15 flex items-center justify-center">
            <Radio className="w-5 h-5 text-nexus-amber" />
          </div>
          <div>
            <div className="label-eyebrow text-nexus-amber">// action required</div>
            <div className="font-display font-black text-lg mt-0.5">Connect LiveKit first</div>
          </div>
        </div>
        <p className="text-sm text-white/70">
          Connect your free LiveKit Cloud account to go live. It takes 60 seconds.
        </p>
        <div className="flex gap-2">
          <button
            onClick={() => { onClose(); onSetupClick?.(); }}
            className="btn-nexus flex-1 justify-center"
            data-testid="livekit-modal-setup-btn"
          >
            <Plug className="w-3.5 h-3.5" /> Set up now
          </button>
          <button onClick={onClose} className="btn-ghost" data-testid="livekit-modal-cancel">
            Later
          </button>
        </div>
      </div>
    </div>
  );
}

export default LiveKitSetupCard;
